import type { Match } from '@/data/types';
import type { EngineParticipant, EngineResult, GeneratedMatch } from './types';
import { computeStandings } from './standings';
import { computeBuchholz } from './swiss-buchholz';

export function defaultSwissRounds(n: number): number {
  if (n <= 1) return 0;
  return Math.ceil(Math.log2(n));
}

function mid(round: number, slot: number): string {
  return `SW-R${round}-S${slot}`;
}

/**
 * Первый тур: top-half vs bottom-half по посеву (или по позиции в массиве).
 */
export function generateSwissRound1(participants: EngineParticipant[]): EngineResult {
  if (participants.length < 2) {
    throw new Error('Нужно минимум 2 участника');
  }
  // Сортировка по seed; неупорядоченные идут в конец
  const sorted = [...participants].sort((a, b) => {
    const sa = a.seed ?? Number.POSITIVE_INFINITY;
    const sb = b.seed ?? Number.POSITIVE_INFINITY;
    return sa - sb;
  });

  const matches: GeneratedMatch[] = [];
  let n = sorted.length;
  let byeId: string | null = null;

  if (n % 2 === 1) {
    // Bye достаётся последнему по посеву
    byeId = sorted[n - 1].id;
    sorted.splice(n - 1, 1);
    n--;
  }

  const half = n / 2;
  for (let i = 0; i < half; i++) {
    matches.push({
      id: mid(1, i + 1),
      stage: 'GROUP',
      round: 1,
      slot: i + 1,
      playerAId: sorted[i].id,
      playerBId: sorted[i + half].id,
    });
  }

  if (byeId) {
    matches.push({
      id: mid(1, half + 1),
      stage: 'GROUP',
      round: 1,
      slot: half + 1,
      playerAId: byeId,
      playerBId: null,
      autoAdvanceWinnerId: byeId,
    });
  }

  return { matches, rounds: 1 };
}

interface Player {
  id: string;
  points: number;
  buchholz: number;
  hadBye: boolean;
}

function hasPlayed(history: Match[], a: string, b: string): boolean {
  return history.some(
    (m) =>
      (m.playerAId === a && m.playerBId === b) ||
      (m.playerAId === b && m.playerBId === a)
  );
}

// Страховка от экспоненциального worst-case: на 128 игроках с патологической
// историей перебор может подвесить вкладку. Лимит входов в рекурсию.
const PAIR_GREEDY_MAX_STEPS = 200000;

function pairGreedy(players: Player[], history: Match[]): [string, string][] | null {
  const counter = { steps: 0 };
  return pairGreedyInner(players, history, counter);
}

function pairGreedyInner(
  players: Player[],
  history: Match[],
  counter: { steps: number }
): [string, string][] | null {
  if (++counter.steps > PAIR_GREEDY_MAX_STEPS) return null; // лимит исчерпан
  if (players.length === 0) return [];
  if (players.length % 2 !== 0) return null;

  const [head, ...rest] = players;
  for (let i = 0; i < rest.length; i++) {
    const candidate = rest[i];
    if (hasPlayed(history, head.id, candidate.id)) continue;
    const remaining = rest.slice(0, i).concat(rest.slice(i + 1));
    const subResult = pairGreedyInner(remaining, history, counter);
    if (subResult !== null) {
      return [[head.id, candidate.id], ...subResult];
    }
  }
  return null; // нет валидной пары для head
}

export function pairNextSwissRound(
  participants: { id: string }[],
  history: Match[],
  nextRound: number
): EngineResult {
  const ids = participants.map((p) => p.id);
  const standings = computeStandings(history, ids);
  const buchholz = computeBuchholz(history, ids);

  // Очки с учётом bye (computeStandings не учитывает матчи без playerBId)
  const points = new Map<string, number>();
  for (const id of ids) {
    const row = standings.find((r) => r.participantId === id)!;
    points.set(id, row.points);
  }
  for (const m of history) {
    if (m.status === 'COMPLETED' && m.score && (!m.playerAId || !m.playerBId)) {
      points.set(m.score.winnerId, (points.get(m.score.winnerId) ?? 0) + 1);
    }
  }

  // Кто получал bye (winnerId === playerAId, playerBId === null)
  const hadByeSet = new Set<string>();
  for (const m of history) {
    if (m.status === 'COMPLETED' && m.playerBId === null && m.playerAId) {
      hadByeSet.add(m.playerAId);
    }
  }

  let players: Player[] = ids.map((id) => ({
    id,
    points: points.get(id) ?? 0,
    buchholz: buchholz.get(id) ?? 0,
    hadBye: hadByeSet.has(id),
  }));

  // Сортировка: points DESC, buchholz DESC, id ASC
  players.sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.buchholz !== a.buchholz) return b.buchholz - a.buchholz;
    return a.id.localeCompare(b.id);
  });

  // Bye для нечётного N: перебираем кандидатов в порядке приоритета.
  // Конкретный выбор bye может сделать остаток непарным (тупик по rematch) —
  // поэтому пробуем каждого, пока pairGreedy не вернёт решение.
  let byeId: string | null = null;
  let pairs: [string, string][] | null;
  if (players.length % 2 === 1) {
    // Приоритет: слабейшие без bye первыми, затем — слабейшие вообще.
    // players отсортированы по силе DESC, значит конец массива = слабейшие.
    const order = [...players].reverse();
    const byeCandidates = [
      ...order.filter((p) => !p.hadBye),
      ...order.filter((p) => p.hadBye),
    ].map((p) => p.id);

    pairs = null;
    for (const candidateId of byeCandidates) {
      const remaining = players.filter((p) => p.id !== candidateId);
      const result = pairGreedy(remaining, history);
      if (result !== null) {
        byeId = candidateId;
        pairs = result;
        break;
      }
    }
  } else {
    pairs = pairGreedy(players, history);
  }

  if (pairs === null) {
    throw new Error('Не удалось спарить — нужно ручное вмешательство');
  }

  const matches: GeneratedMatch[] = pairs.map(([a, b], idx) => ({
    id: mid(nextRound, idx + 1),
    stage: 'GROUP',
    round: nextRound,
    slot: idx + 1,
    playerAId: a,
    playerBId: b,
  }));

  if (byeId) {
    matches.push({
      id: mid(nextRound, matches.length + 1),
      stage: 'GROUP',
      round: nextRound,
      slot: matches.length + 1,
      playerAId: byeId,
      playerBId: null,
      autoAdvanceWinnerId: byeId,
    });
  }

  return { matches, rounds: nextRound };
}
